import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  CreditCard,
  CheckCircle,
  XCircle,
  RefreshCcw,
  AlertTriangle,
  ExternalLink,
  Banknote,
  Send,
  Mail,
  Phone,
  Copy,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PaymentMethodSelector } from '@/components/PaymentMethodSelector';

const STATUS_CONFIG = {
  pending: { label: 'Ожидает', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: RefreshCcw },
  processing: { label: 'Обработка', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 },
  succeeded: { label: 'Оплачен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  failed: { label: 'Ошибка', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  refunded: { label: 'Возврат', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: RefreshCcw },
  cancelled: { label: 'Отменён', color: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', icon: XCircle },
};

function formatAmount(amount, currency = 'USD') {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency
  }).format(amount);
}

export function OrderPaymentWidget({ order, onPaymentComplete }) {
  const { api, hasRole } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [config, setConfig] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(null);

  // Checkout state
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState('create'); // 'create' | 'send'
  const [msgConfig, setMsgConfig] = useState(null); // messaging config

  const canManagePayments = hasRole('admin') || hasRole('manager');

  const fetchPayments = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const [paymentsRes, configRes, msgRes] = await Promise.all([
        api.get(`/orders/${order.id}/payments`),
        api.get('/payments/config'),
        api.get('/checkout/messaging-config').catch(() => ({ data: null })),
      ]);
      setPayments(paymentsRes.data.payments || []);
      setConfig(configRes.data);
      setMsgConfig(msgRes.data);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [api, order?.id]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Create Stripe Checkout session
  const handleCreateCheckout = async () => {
    setCreateLoading(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/checkout/session', {
        order_id: order.id,
        origin_url: origin,
      });

      setCheckoutUrl(res.data.checkout_url);
      setCheckoutStep('send');
      toast.success(`Ссылка на оплату создана: ${formatAmount(res.data.amount)}`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Ошибка создания ссылки';
      toast.error(detail);
    } finally {
      setCreateLoading(false);
    }
  };

  // Send payment link via email or SMS
  const handleSendLink = async (channel) => {
    if (!checkoutUrl) return;
    setSendLoading(channel);
    try {
      const res = await api.post('/checkout/send-link', {
        order_id: order.id,
        checkout_url: checkoutUrl,
        send_via: channel,
      });

      if (res.data.success) {
        if (res.data.stub) {
          toast.info(res.data.message);
        } else {
          toast.success(res.data.message);
        }
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSendLoading(null);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = async () => {
    if (!checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(checkoutUrl);
      toast.success('Ссылка скопирована');
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = checkoutUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Ссылка скопирована');
    }
  };

  // Record manual payment (old flow)
  const handleManualPayment = async () => {
    setCreateLoading(true);
    try {
      await api.post('/payments/intents', {
        order_id: order.id,
        amount: remainingAmount,
        currency: 'USD',
        provider: 'manual',
      });
      toast.success('Платёж записан');
      fetchPayments();
      onPaymentComplete?.();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    try {
      await api.post(`/payments/intents/${paymentId}/mark-paid`);
      toast.success('Отмечено как оплачено');
      fetchPayments();
      onPaymentComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCheckoutUrl(null);
    setCheckoutStep('create');
    if (checkoutUrl) {
      // Refresh payments since a checkout was created
      fetchPayments();
    }
  };

  // Calculate totals
  const totalPaid = payments.filter(p => p.status === 'succeeded').reduce((sum, p) => sum + p.amount, 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);
  const orderAmount = order?.total_amount || 0;
  const remainingAmount = Math.max(0, Math.round((orderAmount - totalPaid) * 100) / 100);
  const isPaidInFull = remainingAmount <= 0;

  if (loading) {
    return (
      <div className="p-4 rounded-lg bg-muted/50 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <CreditCard className="h-4 w-4" />
        {t('payments.title')}
      </h4>

      {/* Payment Summary */}
      <div className="p-4 rounded-lg bg-muted/50 space-y-3">
        {/* Amount Info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">{t('payments.orderAmount')}</p>
            <p className="font-semibold text-lg tabular-nums">{formatAmount(orderAmount)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">{t('payments.paid')}</p>
            <p className={cn("font-semibold text-lg tabular-nums", totalPaid > 0 ? "text-green-600" : "")}>
              {formatAmount(totalPaid)}
            </p>
          </div>
        </div>

        {/* Status */}
        {isPaidInFull ? (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">{t('payments.paidInFull')}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">{t('payments.remaining')}: {formatAmount(remainingAmount)}</span>
            </div>
            {canManagePayments && (
              <Button 
                size="sm" 
                onClick={() => setDialogOpen(true)}
                data-testid="create-order-payment-btn"
              >
                <Send className="h-4 w-4 mr-1" />
                Выставить счёт
              </Button>
            )}
          </div>
        )}

        {/* Pending Payments */}
        {totalPending > 0 && (
          <div className="text-xs text-muted-foreground">
            {t('payments.pendingPayments')}: {formatAmount(totalPending)}
          </div>
        )}
      </div>

      {/* Payment History */}
      {payments.filter(p => p.status !== 'cancelled').length > 0 && (
        <div className="space-y-2">
          {payments.filter(p => p.status !== 'cancelled').slice(0, 3).map(payment => {
            const statusCfg = STATUS_CONFIG[payment.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusCfg.icon;
            return (
              <div 
                key={payment.id}
                className="flex items-center justify-between p-2 rounded-lg bg-background border"
              >
                <div className="flex items-center gap-2">
                  <Badge className={cn("text-xs flex items-center gap-1", statusCfg.color)}>
                    <StatusIcon className="h-3 w-3" />
                    {statusCfg.label}
                  </Badge>
                  <span className="font-mono text-sm">{formatAmount(payment.amount)}</span>
                  <span className="text-xs text-muted-foreground">
                    {payment.provider === 'stripe' ? 'Stripe' : 
                     payment.provider === 'yookassa' ? 'YooKassa' : 'Вручную'}
                  </span>
                  {payment.metadata?.checkout_url && payment.status === 'pending' && (
                    <Button
                      variant="ghost" size="sm" className="h-6 px-1"
                      onClick={() => { setCheckoutUrl(payment.metadata.checkout_url); setCheckoutStep('send'); setDialogOpen(true); }}
                      data-testid={`resend-link-${payment.id}`}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      <span className="text-xs">Ссылка</span>
                    </Button>
                  )}
                </div>
                {payment.status === 'pending' && payment.provider === 'manual' && canManagePayments && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleMarkPaid(payment.id)}
                    data-testid={`mark-paid-${payment.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Оплачен
                  </Button>
                )}
              </div>
            );
          })}
          {payments.filter(p => p.status !== 'cancelled').length > 3 && (
            <p className="text-xs text-muted-foreground text-center">
              +{payments.filter(p => p.status !== 'cancelled').length - 3} ещё
            </p>
          )}
        </div>
      )}

      {/* Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[440px]" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {checkoutStep === 'create' ? 'Выставить счёт' : 'Отправить ссылку на оплату'}
            </DialogTitle>
            <DialogDescription>
              {order?.title} — {formatAmount(remainingAmount)}
            </DialogDescription>
          </DialogHeader>

          {checkoutStep === 'create' && (
            <div className="space-y-4 py-4">
              {/* Invoice summary */}
              <div className="p-3 rounded-lg bg-muted/50 border space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Сумма заказа:</span>
                  <span className="font-semibold">{formatAmount(orderAmount)}</span>
                </div>
                {totalPaid > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Уже оплачено:</span>
                    <span className="text-green-600">{formatAmount(totalPaid)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-semibold border-t pt-2">
                  <span>К оплате:</span>
                  <span className="text-primary">{formatAmount(remainingAmount)}</span>
                </div>
              </div>

              {/* Payment Method Selector */}
              <PaymentMethodSelector
                order={order}
                remainingAmount={remainingAmount}
                api={api}
                onPaymentLink={(link, provider) => {
                  setCheckoutUrl(link);
                  setCheckoutStep('send');
                  toast.success(`Ссылка на оплату создана (${provider})`);
                }}
                onCashPayment={() => {
                  fetchPayments();
                  onPaymentComplete?.();
                  setDialogOpen(false);
                }}
                onStripeCheckout={handleCreateCheckout}
                onDone={() => {
                  fetchPayments();
                  onPaymentComplete?.();
                  setDialogOpen(false);
                }}
              />
            </div>
          )}

          {checkoutStep === 'send' && checkoutUrl && (
            <div className="space-y-4 py-4">
              {/* Link preview */}
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                <div className="flex items-center gap-2 text-emerald-400 font-medium mb-2">
                  <CheckCircle className="h-4 w-4" />
                  Ссылка на оплату создана
                </div>
                <p className="text-xs text-muted-foreground break-all font-mono">{checkoutUrl}</p>
              </div>

              {/* Send options */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Отправить клиенту</Label>
                
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleSendLink('email')}
                  disabled={sendLoading !== null}
                  data-testid="send-via-email-btn"
                >
                  {sendLoading === 'email' ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : <Mail className="mr-3 h-4 w-4 text-blue-400" />}
                  <div className="text-left flex-1">
                    <p className="font-medium">Отправить по Email</p>
                    <p className="text-xs text-muted-foreground">
                      {msgConfig?.email?.enabled
                        ? `Через ${msgConfig.email.provider || 'email'}`
                        : 'На email клиента из карточки'}
                    </p>
                  </div>
                  {msgConfig && !msgConfig.email?.enabled && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-500 border-amber-500/30">stub</Badge>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleSendLink('sms')}
                  disabled={sendLoading !== null}
                  data-testid="send-via-sms-btn"
                >
                  {sendLoading === 'sms' ? <Loader2 className="mr-3 h-4 w-4 animate-spin" /> : <Phone className="mr-3 h-4 w-4 text-green-400" />}
                  <div className="text-left flex-1">
                    <p className="font-medium">Отправить по SMS</p>
                    <p className="text-xs text-muted-foreground">
                      {msgConfig?.sms?.enabled
                        ? `Через ${msgConfig.sms.provider || 'sms'}`
                        : 'На телефон клиента из карточки'}
                    </p>
                  </div>
                  {msgConfig && !msgConfig.sms?.enabled && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-500 border-amber-500/30">stub</Badge>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleCopyLink}
                  data-testid="copy-link-btn"
                >
                  <Copy className="mr-3 h-4 w-4 text-amber-400" />
                  <div className="text-left">
                    <p className="font-medium">Скопировать ссылку</p>
                    <p className="text-xs text-muted-foreground">Отправить самостоятельно</p>
                  </div>
                </Button>

                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => window.open(checkoutUrl, '_blank')}
                  data-testid="open-checkout-btn"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Открыть страницу оплаты
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
